import {
  Machine,
  assign,
  AnyEventObject,
  ConditionPredicate,
  ActionFunctionMap,
  EventObject,
  TransitionConfigOrTarget,
  TransitionsConfig,
} from 'xstate';
import steps from './steps';

export type ProuniContext = {
  history: string[];
};

export const actions: ActionFunctionMap<ProuniContext, AnyEventObject> = {
  updateHistory: assign({
    history: (ctx, event, { state }) => {
      if (event.type !== 'NEXT') return ctx.history;

      const [currentStateMeta]: any[] = Object.values(state?.meta || {});
      const historySet = new Set(ctx.history);
      if (currentStateMeta) historySet.add(currentStateMeta.step);
      return Array.from(historySet);
    },
  }),
};

export const guards: Record<string, ConditionPredicate<ProuniContext, AnyEventObject>> = {
  canNavigateToStep: (ctx, _, { cond }: any) => ctx.history.includes(cond.step),
};

export const returnableSteps = [
  steps.personalData,
  steps.personalDocuments,
  steps.residence,
  steps.familyIncome,
  steps.education,
  steps.acceptance,
];

export const globalEvents: TransitionsConfig<ProuniContext, EventObject> = {
  ...returnableSteps.reduce((acc, step) => {
    const event: string = `GO_TO_${step.toUpperCase()}`;

    acc[event] = { target: step, cond: { type: 'canNavigateToStep', step } };

    return acc;
  }, {} as { [key: string]: TransitionConfigOrTarget<ProuniContext, EventObject> }),
};

export default Machine<ProuniContext>(
  {
    id: 'prouni',
    initial: 'prouniPersonalData',
    context: {
      history: [],
    },
    states: {
      prouniPersonalData: {
        meta: { step: steps.personalData },
        exit: ['updateHistory'],
        on: { NEXT: 'prouniPersonalDocuments' },
      },
      prouniPersonalDocuments: {
        meta: { step: steps.personalDocuments },
        exit: ['updateHistory'],
        on: {
          PREV: 'prouniPersonalData',
          NEXT: 'prouniResidence',
        },
      },
      prouniResidence: {
        meta: { step: steps.residence },
        exit: ['updateHistory'],
        on: {
          PREV: 'prouniPersonalDocuments',
          NEXT: 'prouniFamilyIncome',
        },
      },
      prouniFamilyIncome: {
        meta: { step: steps.familyIncome },
        exit: ['updateHistory'],
        on: {
          PREV: 'prouniResidence',
          NEXT: 'prouniEducation',
        },
      },
      prouniEducation: {
        meta: { step: steps.education },
        exit: ['updateHistory'],
        on: {
          PREV: 'prouniFamilyIncome',
          NEXT: 'prouniAcceptance',
        },
      },
      prouniAcceptance: {
        meta: { step: steps.acceptance },
        exit: ['updateHistory'],
        on: {
          PREV: 'prouniEducation',
        },
      },
    },
    on: {
      ...globalEvents,
    },
  },
  {
    actions,
    guards,
  },
);
